import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "../Input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Label } from "../Label";
import Select from "react-select";
import { useCrudMenuItem } from "@/hooks/menu/useCrudMenuItem";
import { MenuItemModalProps } from "./menu.props.type";
import { BsFillInfoCircleFill } from "react-icons/bs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MenuItemOption } from "@/redux/services/menu/menu.type";

const MenuItemModal: React.FC<MenuItemModalProps> = (props) => {
  const {
    sectionIndex,
    menuItemIndex,
    menuItem,
    isOpen,
    onClose,
    menuItemOptions,
    appendMenuItem,
    getValues,
    setValues,
    section,
  } = props;

  const item = getValues(
    `menuSections.${sectionIndex}.menuItems.${menuItemIndex}`
  );

  const {
    register,
    errors,
    onSubmit,
    onSelectMenuItemImage,
    onSelectMenuItemOption,
    selectedMenuItemOptions,
    selectedMenuItemOptionsWithValues,
    handleChangePriceModifier,
    isLoadFetchMenuItem,
  } = useCrudMenuItem({
    sectionIndex,
    menuItemIndex,
    menuItemId: menuItem?.id,
    sectionId: section?.id,
    close: onClose,
    appendMenuItem,
    setValues,
    item,
    menuItemOptions,
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl pt-5">
        <DialogTitle>{item ? "Modifier l'" : "Ajouter un"} élément</DialogTitle>
        {!isLoadFetchMenuItem ? (
          <React.Fragment>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div>
                <Input
                  type="text"
                  id={`menuSections.${sectionIndex}.menuItems.${menuItemIndex}.title`}
                  label="Nom"
                  placeholder="Nom"
                  {...register("title")}
                  error={errors?.title}
                />
              </div>

              <div>
                <Input
                  type="number"
                  id={`menuSections.${sectionIndex}.menuItems.${menuItemIndex}.price`}
                  placeholder="Prix sur ce menu"
                  label="Prix sur ce menu"
                  {...register("basePrice")}
                  error={errors?.basePrice}
                />
              </div>

              <div>
                <Input
                  type="number"
                  id={`menuSections.${sectionIndex}.menuItems.${menuItemIndex}.sortOrder`}
                  placeholder="Position"
                  label="Position"
                  {...register("sortOrder")}
                  error={errors?.sortOrder}
                  min={1}
                />
              </div>

              <div>
                <label
                  className="text-gray-700"
                  role="button"
                  htmlFor={`menuSections.${sectionIndex}.menuItems.${menuItemIndex}.image`}
                >
                  Image
                </label>
                <div>
                  <input
                    type="file"
                    className="block py-4 px-3 w-full border bg-white rounded-sm mt-1"
                    id={`menuSections.${sectionIndex}.menuItems.${menuItemIndex}.image`}
                    onChange={onSelectMenuItemImage}
                    accept="image/*"
                  />
                  {menuItem?.image && (
                    <img
                      src={menuItem.image?.url}
                      alt={menuItem?.title}
                      className="w-12 h-12 object-contain"
                    />
                  )}
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="description" label="Description" />
              <textarea
                {...register("description")}
                placeholder="Description"
                id={`menuSections.${sectionIndex}.menuItems.${menuItemIndex}.description`}
                className={cn(
                  "block py-2 px-3 w-full border placeholder:text-sm h-28",
                  errors?.description
                    ? "border-danger focus:outline-danger"
                    : "border-gray-200 focus:outline-secondary"
                )}
              />
            </div>
            <section className="mt-4">
              <div className="mb-b">
                {selectedMenuItemOptionsWithValues?.map(
                  (menuItemOption: MenuItemOption, menuItemOptionIndex) => (
                    <fieldset
                      className={cn(
                        "border rounded-md p-4 mb-8",
                        errors &&
                          errors?.itemOptions &&
                          errors?.itemOptions[menuItemOptionIndex]
                          ? "border-red-500"
                          : " border-slate-200"
                      )}
                      key={`menuItemOption-${menuItemOptionIndex}`}
                    >
                      <legend className="px-2 flex items-center">
                        <span className="font-semibold mr-2">
                          {menuItemOption?.title}
                        </span>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <BsFillInfoCircleFill
                                className="text-blue-500"
                                fontSize={16}
                              />
                            </TooltipTrigger>
                            <TooltipContent
                              className="max-w-xs md:max-w-lg shadow-lg p-3"
                              align="start"
                            >
                              <p className="text-sm text-gray-700 leading-normal">
                                Veillez entrer le modificateur pour chaque
                                valeur. Le modificateur de prix est le prix qui
                                sera ajouter ou soustrait au prix de base.
                                Entrer +1000 pour ajouter 1000 au prix de base
                                ou -1000 pour retirer 1000 au prix de base.
                                Entrer 0 pour ne pas modifier le prix de base.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </legend>

                      <div className="grid grid-cols-3 gap-4 mt-4">
                        {menuItemOption?.menuItemOptionValues?.map(
                          (menuItemOptionValue, menuItemOptionValueIndex) => (
                            <div
                              className="col-span-3 md:col-span-1"
                              key={`menu_item_option_value-${menuItemOptionValueIndex}`}
                            >
                              <label
                                htmlFor="petite"
                                className="block text-grayDark text-sm mb-1"
                              >
                                {menuItemOptionValue?.title}
                              </label>
                              <input
                                type="number"
                                id={
                                  menuItemOptionValue?.title +
                                  menuItemOptionValueIndex
                                }
                                className="border border-gray-200 w-full py-2 px-2 text-gray-600 text-sm focus:outline-secondary"
                                placeholder="+/-1000"
                                {...register(
                                  `itemOptions.${menuItemOptionIndex}.itemOptionPrices.${menuItemOptionValueIndex}.priceModifier`
                                )}
                                onChange={(e) =>
                                  handleChangePriceModifier(
                                    e,
                                    menuItemOptionValue?.id,
                                    menuItemOptionIndex,
                                    menuItemOptionValueIndex
                                  )
                                }
                              />
                            </div>
                          )
                        )}
                      </div>
                    </fieldset>
                  )
                )}
              </div>
              <div>
                <Label label="Options" />
                <Select
                  placeholder="Sélectionner les options"
                  options={menuItemOptions}
                  value={selectedMenuItemOptions}
                  onChange={onSelectMenuItemOption}
                  isMulti={true}
                  closeMenuOnSelect={false}
                  classNamePrefix="cs-select"
                />
              </div>
            </section>
          </React.Fragment>
        ) : (
          <div className="text-center">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="animate-pulse h-10 bg-slate-200 rounded"></div>
              <div className="animate-pulse h-10 bg-slate-200 rounded"></div>
              <div className="animate-pulse h-10 bg-slate-200 rounded"></div>
              <div className="animate-pulse h-10 bg-slate-200 rounded"></div>
              <div className="animate-pulse h-40 bg-slate-200  col-span-2"></div>
              <div className="animate-pulse h-10 bg-slate-200 rounded col-span-2"></div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            size="sm"
            onClick={onSubmit}
          >
            {item ? "Modifier" : "Ajouter"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MenuItemModal;
